import { useEffect, useMemo, useState, type ReactElement } from "react";
import type { DashboardSnapshot, HealthView } from "../api/client.js";
import { FeedHealth } from "../components/feed-health.js";
import { bookmakerLink } from "../components/bookmaker-link.js";
import {
  recommendedBook,
  type BookAccount,
} from "../components/place-bet-dialog.js";
import { Matchup } from "../components/matchup.js";
import { normalizeLiveLedger } from "./ledger.js";
import {
  Figure,
  KpiStrip,
  Tile,
  money,
  signedUnits,
  winRateReading,
} from "../components/tile.js";
import {
  betCaption,
  ChartControls,
  resolvedAt,
  UnitsChart,
  useChartState,
  type UnitsPoint,
} from "../components/units-chart.js";
import { melbourneDay } from "../melbourne.js";

/**
 * The home board.
 *
 * One screen, no page scroll: everything worth knowing on arrival, without
 * choosing a tab first. What has to be placed and how long is left, what is
 * running right now, what the money has done against what it should have
 * done, and which rules produced it.
 *
 * The board sizes itself to the viewport and lets individual tiles scroll, so
 * a long queue can never push the state of the bank off the bottom. That
 * ordering is deliberate: the figures that decide whether to bet at all must
 * not be the ones that disappear.
 */

const MINUTE = 60_000;

/** Rows arrive as raw database records or prototype fixtures; the shared
 * ledger normaliser resolves both into one shape. */
type HomeLedgerRow = Record<string, unknown>;

function minutesUntil(tipUtc: string | undefined, now: number): number | null {
  if (tipUtc === undefined) return null;
  const tip = Date.parse(tipUtc);
  if (Number.isNaN(tip)) return null;
  return Math.round((tip - now) / MINUTE);
}

function countdown(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 0) return "Started";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** Inside twenty minutes a price is most likely to move or vanish. Amber, not
 * red: nothing has been lost yet. */
function urgencyClass(minutes: number | null): string {
  if (minutes === null) return "";
  if (minutes < 0) return "gone";
  if (minutes <= 20) return "urgent";
  return "";
}

/**
 * "BOS @ NYK" is all the identity a recommendation carries, and it is enough:
 * the two abbreviations key the team marks.
 */
function teamsFromLabel(
  label: string,
): { readonly away: string; readonly home: string } | null {
  const parts = label.split("@").map((part) => part.trim());
  if (parts.length !== 2) return null;
  const [away, home] = parts;
  if (away === undefined || home === undefined || away === "" || home === "")
    return null;
  return { away, home };
}

function GameMarks({
  sport,
  gameLabel,
}: {
  readonly sport: string;
  readonly gameLabel: string;
}): ReactElement {
  const teams = teamsFromLabel(gameLabel);
  if (teams === null) return <span className="matchup-text">{gameLabel}</span>;
  return (
    <Matchup
      sport={sport}
      awayName={teams.away}
      homeName={teams.home}
      awayAbbreviation={teams.away}
      homeAbbreviation={teams.home}
    />
  );
}

/**
 * Median minutes from alert to placement over the last seven days. Every
 * minute a signal waits, its price can move away; one in five MLB signals was
 * gone before placing in September, at a median wait of over three hours.
 */
export function placementSpeed(
  rows: readonly Record<string, unknown>[],
  now: number,
): { placed: number; medianMinutes: number; within15: number } {
  const minutes = rows
    .map((row) => {
      const time = (value: unknown) =>
        typeof value === "string" ? Date.parse(value) : Number.NaN;
      const placed = time(row.placed_at);
      const alerted = time(row.signal_at ?? row.created_at);
      return Number.isFinite(placed) &&
        Number.isFinite(alerted) &&
        now - placed <= 7 * 86_400_000
        ? (placed - alerted) / 60_000
        : null;
    })
    .filter((value): value is number => value !== null && value >= 0)
    .toSorted((a, b) => a - b);
  return {
    placed: minutes.length,
    medianMinutes: minutes[Math.floor(minutes.length / 2)] ?? 0,
    within15: minutes.filter((value) => value <= 15).length,
  };
}

/**
 * Today and this week in dollars (Moss, 3 Nov 2026). The ground rules judge
 * the week, so the week sits beside the day. Weeks start Monday, Melbourne.
 */
export function periodResults(
  rows: readonly {
    readonly placedAt: string;
    readonly settledAt?: string | null;
    readonly result: string | null;
    readonly stakeCents: number;
    readonly actualPrice: number;
  }[],
  now: number,
): Record<"today" | "week", { cents: number; wins: number; losses: number }> {
  const today = melbourneDay(new Date(now));
  const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
  const monday = new Date(
    Date.parse(`${today}T00:00:00Z`) - weekday * 86_400_000,
  )
    .toISOString()
    .slice(0, 10);
  const tally = (from: string) => {
    const done = rows.filter(
      (row) =>
        (row.result === "WIN" || row.result === "LOSS") &&
        melbourneDay(resolvedAt(row)) >= from,
    );
    return {
      cents: Math.round(
        done.reduce(
          (sum, row) =>
            sum +
            (row.result === "WIN"
              ? row.stakeCents * (row.actualPrice - 1)
              : -row.stakeCents),
          0,
        ),
      ),
      wins: done.filter((row) => row.result === "WIN").length,
      losses: done.filter((row) => row.result === "LOSS").length,
    };
  };
  return { today: tally(today), week: tally(monday) };
}

const signedMoney = (cents: number) =>
  `${cents >= 0 ? "+" : "-"}${money(Math.abs(cents))}`;

export function HomePage({
  snapshot,
  ledger,
  health,
  sport,
  feedSport,
  onOpenBoard,
  books = [],
  onPlace,
  marketAgreement,
}: {
  readonly snapshot: DashboardSnapshot;
  readonly ledger: readonly HomeLedgerRow[];
  readonly health: HealthView | undefined;
  readonly sport: string;
  /** The sport whose per-sport feeds are reported, which is not the selection
   * when sports are combined. */
  readonly feedSport: string;
  readonly onOpenBoard: () => void;
  /** Account state per book, so each queued bet shows where the money is. */
  readonly books?: readonly BookAccount[];
  /** Opens the placement dialog for one bet, the same one an alert link opens. */
  readonly onPlace?: (key: string) => void;
  /** Whole-market moves on live bets, toward and against our side. */
  readonly marketAgreement?: { toward: number; against: number } | undefined;
}): ReactElement {
  const chart = useChartState();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const queue = useMemo(
    () =>
      snapshot.recommendations
        .filter((rec) => rec.status === "RECOMMENDED")
        .map((rec) => ({ rec, minutes: minutesUntil(rec.tipUtc, now) }))
        .sort((left, right) => {
          // An unknown deadline must never outrank a real one.
          if (left.minutes === null) return 1;
          if (right.minutes === null) return -1;
          return left.minutes - right.minutes;
        }),
    [now, snapshot.recommendations],
  );

  /**
   * What the value gate turned down, closest first.
   *
   * Roughly a third of recommendations die here, and until now none of them
   * were visible anywhere. That makes a night with no edge in it
   * indistinguishable from a gate set slightly too tight - which is the
   * question that keeps coming up about volume. Showing the shortfall answers
   * it: a board full of bets a cent short reads very differently from one
   * where nothing came close.
   *
   * It is deliberately not actionable. These are not bets to place; the
   * minimum is the price below which there is no edge left, and taking one
   * anyway is how a system like this quietly starts losing.
   */
  const missed = useMemo(
    () =>
      snapshot.recommendations
        .filter((rec) => rec.status !== "RECOMMENDED")
        .map((rec) => ({
          rec,
          short: rec.minimumOdds - rec.price,
          waiting: rec.status === "AWAITING_LINEUP",
        }))
        .sort((left, right) => {
          if (left.waiting !== right.waiting) return left.waiting ? -1 : 1;
          return left.short - right.short;
        }),
    [snapshot.recommendations],
  );

  const rows = useMemo(() => normalizeLiveLedger(ledger), [ledger]);
  const open = useMemo(
    () => rows.filter((row) => row.status === "PLACED"),
    [rows],
  );
  const settled = useMemo(
    () => rows.filter((row) => row.status === "SETTLED"),
    [rows],
  );

  const profitUnits = useMemo(
    () => settled.reduce((total, row) => total + (row.profitUnits ?? 0), 0),
    [settled],
  );

  const graded = useMemo(
    () =>
      settled.filter((row) => {
        const result = row.result;
        return result === "WIN" || result === "LOSS";
      }),
    [settled],
  );
  const wins = useMemo(
    () => graded.filter((row) => row.result === "WIN").length,
    [graded],
  );

  const byRule = useMemo(() => {
    const rules = new Map<
      string,
      {
        bets: number;
        units: number;
        wins: number;
      }
    >();
    for (const row of settled) {
      const rule = row.ruleVersion || "Unattributed";
      const current = rules.get(rule) ?? {
        bets: 0,
        units: 0,
        wins: 0,
      };
      rules.set(rule, {
        bets: current.bets + 1,
        units: current.units + (row.profitUnits ?? 0),
        wins: current.wins + (row.result === "WIN" ? 1 : 0),
      });
    }
    // Most-bet first. A rule is judged against its own backtest on its
    // Analytics page; here the order is just how much each one has done.
    return [...rules.entries()]
      .map(([rule, stat]) => ({ rule, ...stat }))
      .sort(
        (left, right) => right.bets - left.bets || right.units - left.units,
      );
  }, [settled]);

  const curve = useMemo<readonly UnitsPoint[]>(() => {
    // One point per bet. The chart collapses these into days, weeks or months
    // itself, and the bet-by-bet grain needs them uncollapsed - which is why
    // this no longer does its own bucketing.
    let running = 0;
    return [...settled]
      .sort((left, right) => resolvedAt(left).localeCompare(resolvedAt(right)))
      .map((row) => {
        running += row.profitUnits ?? 0;
        return {
          day: melbourneDay(resolvedAt(row)),
          units: running,
          caption: betCaption(row),
        };
      });
  }, [settled]);

  // How far below its best the bank sits now, and the worst it has been.
  const drawdown = useMemo(() => {
    let peak = 0;
    let worst = 0;
    for (const point of curve) {
      peak = Math.max(peak, point.units);
      worst = Math.min(worst, point.units - peak);
    }
    const last = curve.at(-1)?.units ?? 0;
    return { current: last - peak, worst };
  }, [curve]);
  const moved =
    (marketAgreement?.toward ?? 0) + (marketAgreement?.against ?? 0);

  const speed = useMemo(() => placementSpeed(ledger, now), [ledger, now]);
  const period = useMemo(() => periodResults(rows, now), [rows, now]);

  const exposureShare =
    snapshot.bankrollCents === 0
      ? 0
      : Math.min((snapshot.committedCents / snapshot.bankrollCents) * 100, 100);

  return (
    <div className="home-grid">
      {/* The state of the bank, pinned to the top row so a long queue can
          never push it off the screen. */}
      <KpiStrip columns={2}>
        {(["today", "week"] as const).map((key) => (
          <Figure
            key={key}
            label={key === "today" ? "Today" : "This week"}
            value={signedMoney(period[key].cents)}
            tone={
              period[key].wins + period[key].losses === 0
                ? undefined
                : period[key].cents >= 0
                  ? "positive"
                  : "negative"
            }
            sub={`${String(period[key].wins)}–${String(period[key].losses)}`}
          />
        ))}
      </KpiStrip>
      <KpiStrip columns={7}>
        <Figure label="Bankroll" value={money(snapshot.bankrollCents)} />
        <Figure
          label="At risk"
          value={money(snapshot.committedCents)}
          tone={snapshot.committedCents > 0 ? "warning" : undefined}
          sub={`${exposureShare.toFixed(1)}% of bank · ${open.length} open`}
        />
        <Figure
          label="Profit"
          value={signedUnits(profitUnits)}
          tone={profitUnits >= 0 ? "positive" : "negative"}
          sub={`${settled.length} settled`}
        />
        <Figure
          label="Drawdown"
          value={signedUnits(drawdown.current)}
          tone={drawdown.current < 0 ? "negative" : undefined}
          sub={`worst ${signedUnits(drawdown.worst)}`}
        />
        <Figure
          label="Market agreed"
          value={
            moved === 0
              ? "—"
              : `${Math.round(((marketAgreement?.toward ?? 0) / moved) * 100)}%`
          }
          sub={
            moved === 0
              ? "none measured"
              : `${marketAgreement?.toward ?? 0} toward · ${marketAgreement?.against ?? 0} against`
          }
        />
        <Figure
          label="Strike rate"
          value={
            graded.length === 0
              ? "—"
              : `${Math.round((wins / graded.length) * 100)}%`
          }
          sub={
            graded.length === 0 ? "Nothing graded" : `${wins}/${graded.length}`
          }
        />
        <Figure
          label="Time to place"
          value={
            speed.placed === 0 ? "—" : `${Math.round(speed.medianMinutes)} min`
          }
          tone={
            speed.placed === 0
              ? undefined
              : speed.medianMinutes <= 15
                ? "positive"
                : "warning"
          }
          sub={
            speed.placed === 0
              ? "nothing placed in 7 days"
              : `median, 7 days · ${speed.within15} of ${speed.placed} within 15 min`
          }
        />
      </KpiStrip>

      <Tile
        className="tile-queue"
        title="Next to place"
        note={
          queue.length === 0
            ? "Nothing waiting"
            : `${queue.length} waiting · soonest first`
        }
        span={5}
      >
        {queue.length === 0 ? (
          <p className="tile-empty">
            No qualified bets right now. The board refreshes as prices move.
          </p>
        ) : (
          <ul className="queue">
            {queue.map(({ rec, minutes }) => (
              <li key={rec.signalId} className={urgencyClass(minutes)}>
                <span className="queue-clock tabular">
                  {countdown(minutes)}
                </span>
                <span className="queue-body">
                  <strong>{rec.description}</strong>
                  <small>{rec.ruleName ?? rec.ruleVersion}</small>
                </span>
                <span className="queue-game">
                  <GameMarks
                    sport={rec.sport ?? sport}
                    gameLabel={rec.gameLabel}
                  />
                </span>
                <span className="queue-price tabular">
                  <strong>{rec.price.toFixed(2)}</strong>
                  <small>min {rec.minimumOdds.toFixed(2)}</small>
                </span>
                <span className="queue-actions">
                  {(() => {
                    // The link and book are the router's pick, so the queue sends
                    // the bet to the router's book; the best price is named
                    // underneath only when it is somewhere else.
                    const pick = recommendedBook(rec, books);
                    const href = bookmakerLink(pick.bookmaker, rec.offers);
                    const account = books.find(
                      (book) => book.bookmaker === pick.bookmaker,
                    );
                    const note = (
                      <>
                        {account === undefined
                          ? null
                          : ` · ${money(account.availableCents)} there`}
                        {pick.bookmaker === rec.bookmaker
                          ? null
                          : ` · ${pick.price.toFixed(2)} here, best ${rec.price.toFixed(2)} at ${rec.bookmaker}`}
                      </>
                    );
                    return href === null ? (
                      <span className="queue-stake tabular">
                        {money(rec.stakeCents)}
                        <small>
                          {pick.bookmaker}
                          {note}
                        </small>
                      </span>
                    ) : (
                      <a
                        className="queue-stake tabular is-link"
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        title={`Open ${pick.bookmaker}`}
                      >
                        {money(rec.stakeCents)}
                        <small>
                          {pick.bookmaker} ↗{note}
                        </small>
                      </a>
                    );
                  })()}
                  {onPlace === undefined ? null : (
                    <button
                      type="button"
                      className="queue-place"
                      onClick={() => onPlace(rec.naturalKey ?? rec.signalId)}
                    >
                      Log bet
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <button type="button" className="button" onClick={onOpenBoard}>
          Open the full board
        </button>
      </Tile>

      <Tile
        title="Just missed"
        note={
          missed.length === 0 ? "Nothing turned down" : `${missed.length} short`
        }
        span={3}
      >
        {missed.length === 0 ? (
          <p className="tile-empty">
            Everything the rules found cleared its minimum.
          </p>
        ) : (
          <ul className="missed-list">
            {missed.slice(0, 8).map(({ rec, short, waiting }) => (
              <li key={rec.signalId}>
                <span className="missed-body">
                  <strong>{rec.description}</strong>
                  <small>{rec.gameLabel}</small>
                </span>
                {waiting ? (
                  <span className="missed-gap is-warning">Lineup</span>
                ) : (
                  <span className="missed-gap tabular">
                    <strong>{rec.price.toFixed(2)}</strong>
                    <small>needs {rec.minimumOdds.toFixed(2)}</small>
                  </span>
                )}
                <span
                  className={`missed-short tabular ${short <= 0.05 ? "is-warning" : ""}`}
                >
                  {waiting ? "—" : `-${short.toFixed(2)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Tile>

      <Tile
        title="Running now"
        note={open.length === 0 ? "Nothing open" : `${open.length} live`}
        span={4}
      >
        {open.length === 0 ? (
          <p className="tile-empty">
            No money on the table. Placed bets appear here until they settle.
          </p>
        ) : (
          <ul className="queue live">
            {open.map((row, index) => {
              const progress = row.liveProgress;
              const innings = progress?.inningsPlayed ?? null;
              const half = progress?.inningHalf ?? null;
              const home = progress?.homeScore ?? null;
              const away = progress?.awayScore ?? null;
              const actual = progress?.actualValue ?? null;
              const opportunity = progress?.opportunityLabel ?? "";
              const started = progress?.status === "IN_PROGRESS";
              return (
                <li key={row.betId || String(index)}>
                  <span className="queue-clock tabular">
                    {innings === null
                      ? started
                        ? "Live"
                        : "Soon"
                      : `${half === "TOP" ? "▲" : "▼"}${innings}`}
                  </span>
                  <span className="queue-body">
                    <strong>{row.description}</strong>
                    <small>
                      {actual === null
                        ? row.ruleVersion
                        : `At ${actual}${opportunity === "" ? "" : ` · ${opportunity}`}${
                            progress?.expectedResult === "WIN"
                              ? " · expected win"
                              : progress?.expectedResult === "LOSS"
                                ? " · expected loss"
                                : ""
                          }`}
                    </small>
                    {row.liveNote ? (
                      <small className="live-note">{row.liveNote}</small>
                    ) : null}
                  </span>
                  <span className="queue-game">
                    <GameMarks sport={sport} gameLabel={row.gameLabel} />
                  </span>
                  <span className="queue-price tabular">
                    <strong>{row.actualPrice.toFixed(2)}</strong>
                    <small>
                      {away === null || home === null
                        ? "No score"
                        : `${away}–${home}`}
                    </small>
                  </span>
                  <span className="queue-stake tabular">
                    {money(row.stakeCents)}
                    <small>to {money(row.stakeCents * row.actualPrice)}</small>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Tile>

      <Tile
        title="Profit over time"
        note="Cumulative, in units"
        actions={<ChartControls state={chart} />}
        span={8}
        fills
      >
        <UnitsChart
          data={curve.length === 0 ? [{ day: "No bets", units: 0 }] : curve}
          state={chart}
        />
      </Tile>

      <div className="tile-stack">
        <Tile
          title="By rule"
          note={byRule.length === 0 ? "Nothing settled" : "Settled only"}
        >
          {byRule.length === 0 ? (
            <p className="tile-empty">
              Rules appear here once their bets settle.
            </p>
          ) : (
            <ul className="rule-list">
              {byRule.map((stat) => (
                <li
                  key={stat.rule}
                  title={winRateReading(stat.wins, stat.bets)}
                >
                  <span className="rule-name">{stat.rule}</span>
                  <span className="rule-bets tabular">{stat.bets}</span>
                  <strong className="tabular">
                    {Math.round((stat.wins / stat.bets) * 100)}%
                  </strong>
                  <span
                    className={`rule-bets tabular ${stat.units >= 0 ? "is-positive" : "is-negative"}`}
                  >
                    {signedUnits(stat.units)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="tile-caveat">
            Bets, strike rate and units. Each rule is judged against its own
            backtest: open it in Analytics for its page and checkpoints.
          </p>
        </Tile>

        <Tile
          title="Feeds"
          note={
            sport === "ALL"
              ? `Allowance shared · ${feedSport} components`
              : "Suppliers and allowance"
          }
        >
          <FeedHealth health={health} sport={feedSport} now={now} />
        </Tile>
      </div>
    </div>
  );
}
